import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './ProductsShowcase.css';
import productsJson from '../../data/Products.json';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import BagBuilder from '../BagBuilder/BagBuilder';
import ProductsLoader from './ProductsLoader';
import { getProductSku } from '../../utils/productsStorage';
// Removed loadProducts import - using productsJson directly
import { useTranslation } from 'react-i18next';
import backArrowIcon from '../../assets/back-arrow.svg';
import underlineIcon from '../../assets/e4859c20b6b2711a7c6bfdc3dbefb5ea9ea640ad.svg';
import { trackSelectProduct, trackConfirmSelection, trackPageView, updateDataLayer, trackClick } from '../../analytics';
import GalleryLoader from '../GalleryLoader/GalleryLoader';

// Webpack (CRA) friendly resolver using require.context
// Expects JSON image paths like 'assets/bags_v1/<folder>/<file>.jpg'
// Use recursive require.context to access all subdirectories
// Support both .jpg and .tif files
const bagsCtxJpg = require.context('../../assets/main_assets/Bags', true, /\.jpg$/i);
const bagsCtxTif = require.context('../../assets/main_assets/Bags', true, /\.tif$/i);
const newBagsCtxJpg = require.context('../../assets/new_bags_march2026', true, /\.jpg$/i);
const newBagsCtxPng = require.context('../../assets/new_bags_march2026', true, /\.png$/i);

// Cache for keys to avoid repeated lookups
const jpgKeys = bagsCtxJpg.keys();
const tifKeys = bagsCtxTif.keys();
const newBagsKeys = [...newBagsCtxJpg.keys(), ...newBagsCtxPng.keys()];

const resolveGallery = (p) => {
  // If it's already a base64 data URL, return it as is
  if (p && p.startsWith('data:')) {
    return p;
  }
  if (!p || p === '') {
    return p; // Return empty string as is
  }

  // Handle new_bags_march2026 assets
  if (p.startsWith('assets/new_bags_march2026/')) {
    const relativePath = p.replace(/^assets\/new_bags_march2026\//, '');
    const key = `./${relativePath}`;
    const isPng = relativePath.toLowerCase().endsWith('.png');
    try {
      return isPng ? newBagsCtxPng(key) : newBagsCtxJpg(key);
    } catch (e) {
      // Fallback: search by filename across both contexts
      const fileName = relativePath.split('/').pop().toLowerCase();
      const matchingKey = newBagsKeys.find(k => k.split('/').pop().toLowerCase() === fileName);
      if (matchingKey) {
        try {
          return matchingKey.toLowerCase().endsWith('.png')
            ? newBagsCtxPng(matchingKey)
            : newBagsCtxJpg(matchingKey);
        } catch (e2) {}
      }
      console.warn('Failed to resolve new bag image:', p);
      return '';
    }
  }

  // Extract the path relative to Bags folder
  // p format: "assets/bags_v1/01__8W2B0T31ABM0NO_NERO/8W2B0T31ABM0NO_SF.jpg"
  // We need: "./01__8W2B0T31ABM0NO_NERO/8W2B0T31ABM0NO_SF.jpg" for require.context
  let relativePath = p.replace(/^assets\/bags_v1\//, '').replace(/^assets\/main_assets\/Bags\//, '');
  const fileName = relativePath.split('/').pop(); // Get just the filename

  // First, try to find by filename (case-insensitive) - this handles spaces and case issues
  const allKeys = [...jpgKeys, ...tifKeys];
  const matchingKey = allKeys.find(k => {
    const keyFileName = k.split('/').pop().toLowerCase();
    return keyFileName === fileName.toLowerCase();
  });

  if (matchingKey) {
    try {
      if (matchingKey.toLowerCase().endsWith('.jpg')) {
        return bagsCtxJpg(matchingKey);
      } else if (matchingKey.toLowerCase().endsWith('.tif')) {
        return bagsCtxTif(matchingKey);
      }
    } catch (e) {
      console.warn('Failed to load matched image:', matchingKey, e);
    }
  }

  // Fallback: try direct path with ./ prefix (require.context format)
  const key = relativePath.startsWith('./') ? relativePath : `./${relativePath}`;
  try {
    if (p.toLowerCase().endsWith('.jpg')) {
      return bagsCtxJpg(key);
    } else if (p.toLowerCase().endsWith('.tif')) {
      return bagsCtxTif(key);
    }
  } catch (e) {
    // Silent fail, will try next method
  }

  // Last resort: try both contexts
  try {
    return bagsCtxJpg(key);
  } catch {
    try {
      return bagsCtxTif(key);
    } catch (e) {
      console.warn('Failed to resolve image:', p, 'tried key:', key);
      // Return empty string to prevent browser from fetching from server
      return '';
    }
  }
};

// Initial products from JSON - extract SKU from original paths BEFORE resolveGallery overwrites them
const initialProducts = productsJson.map(p => ({
  ...p,
  sku: getProductSku(p),
  image: resolveGallery(p.image),
  slides: (p.slides || []).map(resolveGallery),
}));

const ProductsShowcase = ({ initialInitials, onPsInnerActive }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [products, setProducts] = useState(initialProducts);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showHero, setShowHero] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [loadingScreenVisible, setLoadingScreenVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1366);
  const [selectedProductIdx, setSelectedProductIdx] = useState(null); // Track selected product for BagBuilder
  const [showProductsLoader, setShowProductsLoader] = useState(false); // Disabled loader for ps-section--visible
  const [galleryReady, setGalleryReady] = useState(false);
  const loaderKeyRef = useRef(0);
  const imgRefs = useRef([]);
  const targetImgRef = useRef(null);
  const detailRightRef = useRef(null);
  const detailFixedPanelRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Always use products from JSON file, ignore localStorage
    // Clear localStorage to prevent old cached data
    try {
      localStorage.removeItem('valentino_products');
    } catch (e) {
      // Ignore errors
    }
    
    // Use products directly from JSON
    const processedProducts = productsJson.map(p => ({
      ...p,
      sku: getProductSku(p), // extract SKU from original path before resolveGallery overwrites image
      image: resolveGallery(p.image),
      slides: (p.slides || []).map(resolveGallery),
    }));
    setProducts(processedProducts);
  }, []);

  // Handle products loader completion
  const handleProductsLoaderComplete = () => {
    setShowProductsLoader(false);
    // Add a small delay before starting the animation for smoother transition
    setTimeout(() => {
      requestAnimationFrame(() => setIsVisible(true));
    }, 200);
  };

  // Show section immediately without loader
  useEffect(() => {
    if (!showProductsLoader) {
      setIsVisible(true);
    }
  }, [showProductsLoader]);

  // Collect all image URLs for preloading
  const getAllImageUrls = () => {
    const urls = [];
    products.forEach(product => {
      if (product.image) urls.push(product.image);
      if (product.slides && Array.isArray(product.slides)) {
        product.slides.forEach(slide => {
          if (slide) urls.push(slide);
        });
      }
    });
    return urls;
  };

  // Preload all product images (thumbnails + slides) in background after gallery is visible
  useEffect(() => {
    if (!isVisible || products.length === 0) return;
    const urls = getAllImageUrls();
    urls.forEach(url => {
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [isVisible, products]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add delay before showing BagBuilder to match LoadingScreen appearance
  useEffect(() => {
    if (showLoading) {
      // LoadingScreen appears after ~250ms (50ms + 200ms), so delay BagBuilder rendering
      // Add 1 second additional delay before showing BagBuilder
      const timer = setTimeout(() => {
        setLoadingScreenVisible(true);
      }, 1300); // 300ms for LoadingScreen appearance + 1000ms additional delay
      return () => clearTimeout(timer);
    } else {
      setLoadingScreenVisible(false);
    }
  }, [showLoading]);

  const isDetail = showDetail && selectedIdx !== null;

  // Notify parent when ps-inner screen is active
  useEffect(() => {
    if (onPsInnerActive) {
      const isPsInnerActive = !isDetail && !showLoading && !showBuilder && !loadingScreenVisible;
      onPsInnerActive(isPsInnerActive);
    }
  }, [isDetail, showLoading, showBuilder, loadingScreenVisible, onPsInnerActive]);

  // Track mobile and tablet viewport size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1366);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelect = (idx) => {
    const imgEl = imgRefs.current[idx];
    if (!imgEl) return;
    const product = products[idx];
    if (product) {
      const sku = getProductSku(product);
      console.log(
        `%c[ANALYTICS] select_product idx=${idx} sku=${sku}`,
        'color: cyan; font-weight: bold',
        { idx, sku, product_id: product.id, product_sku_field: product.sku, image: product.image }
      );
      trackSelectProduct(sku);
    }
    
    setSelectedIdx(idx);
    setSelectedProductIdx(idx); // Save selection for visual indication and BagBuilder

    // For tablet and mobile, set showRight before showDetail to ensure element is rendered
    // For desktop, reset showRight
    if (isTablet || isMobile) {
      setShowRight(true);
    } else {
      setShowRight(false);
    }
    
    setShowCopy(false);
    setShowHero(false);

    // Scroll to top first to ensure correct positioning before layout change
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    
    setShowDetail(true);
    
    // For mobile devices, show fixed panel immediately
    if (isMobile && !isTablet) {
      setTimeout(() => {
        setShowRight(true);
        if (detailFixedPanelRef.current) {
          detailFixedPanelRef.current.classList.add('is-visible');
        }
      }, 50);
    }
    
    console.log('[ProductsShowcase] handleSelect completed - isTablet:', isTablet, 'showDetail:', true, 'showRight:', isTablet);
    
    if (product) {
      updateDataLayer({
        page_type: 'product_detail',
        pagename: `/products/${getProductSku(product)}`
      });
      trackPageView({
        page_type: 'product_detail',
        pagename: `/products/${getProductSku(product)}`
      });
    }
    
    // For desktop, show hero and copy immediately
    if (!isTablet && !isMobile) {
      setTimeout(() => {
        setShowHero(true);
        setTimeout(() => setShowCopy(true), 250);
      }, 50);
    }

    setShowRight(true);
  };

  // Tablet animation effect - runs when showDetail becomes true
  useEffect(() => {
    console.log('[ProductsShowcase] useEffect triggered - showDetail:', showDetail, 'isTablet:', isTablet, 'targetImgRef:', !!targetImgRef.current, 'detailRightRef:', !!detailRightRef.current);
    
    if (showDetail && isTablet) {
      // Use multiple attempts to find elements
      const attemptAnimation = (attempt = 0) => {
        if (targetImgRef.current && detailRightRef.current) {
          // Fixed panel is optional, so we don't require it
          console.log('[ProductsShowcase] Elements found, starting animation on attempt', attempt);
          
          // Force reflow
          void targetImgRef.current.offsetHeight;
          void detailRightRef.current.offsetHeight;
          
          // Clear any existing GSAP animations
          gsap.killTweensOf(targetImgRef.current);
          gsap.killTweensOf(detailRightRef.current);
          if (detailFixedPanelRef.current) {
            gsap.killTweensOf(detailFixedPanelRef.current);
          }
          
          // Clear any inline styles that might interfere
          targetImgRef.current.style.opacity = '';
          targetImgRef.current.style.transform = '';
          targetImgRef.current.style.transformOrigin = '';
          detailRightRef.current.style.opacity = '';
          detailRightRef.current.style.transform = '';
          if (detailFixedPanelRef.current) {
            detailFixedPanelRef.current.style.opacity = '';
            detailFixedPanelRef.current.style.transform = '';
            detailFixedPanelRef.current.classList.add('is-visible');
          }

          setShowHero(true);
          setTimeout(() => setShowCopy(true), 250);
          
          return true;
        }
        return false;
      };
      
      // Try immediately
      if (!attemptAnimation()) {
        // Try after short delay
        const timer1 = setTimeout(() => {
          if (!attemptAnimation(1)) {
            // Try after longer delay
            const timer2 = setTimeout(() => {
              if (!attemptAnimation(2)) {
                console.warn('[ProductsShowcase] Failed to find elements for animation after 3 attempts');
                // Fallback - show without animation
                setShowHero(true);
                setTimeout(() => setShowCopy(true), 250);
              }
            }, 200);
            
            return () => clearTimeout(timer2);
          }
        }, 100);
        
        return () => clearTimeout(timer1);
      }
    }
  }, [showDetail, isTablet]);


  const handleBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackClick('navigation', 'back_to_all_styles');
    setShowDetail(false);
    setSelectedIdx(null);
    setShowRight(false);
    setShowCopy(false);
    setShowHero(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    // Fire virtual_page_load for the products list page on landing back
    // (mirrors the page view sent when first entering the products stage).
    updateDataLayer({
      page_template: 'products',
      page_type: 'products_list',
      pagename: '/products'
    });
    trackPageView({
      page_template: 'products',
      page_type: 'products_list',
      pagename: '/products'
    });

    // Show products list again
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  };

  const handleConfirm = () => {
    const product = products[selectedIdx];
    if (product) {
      const sku = getProductSku(product);
      console.log('%c[ANALYTICS] confirm_selection', 'color: cyan; font-weight: bold', { sku, initials: initialInitials, product });
      trackConfirmSelection(sku, initialInitials || '');
    }
    // Save the selected product index before showing loading
    setSelectedProductIdx(selectedIdx);
    setShowLoading(true);
  };

  const handleLoadingComplete = () => {
    setShowLoading(false);
    setShowBuilder(true);
    setShowDetail(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  return (
    <>
      {/* Gallery image preloader */}
      {!galleryReady && !showLoading && !showBuilder && !loadingScreenVisible && products.length > 0 && (
        <GalleryLoader
          images={products.map(p => p.image).filter(Boolean).slice(0, 8)}
          onComplete={() => setGalleryReady(true)}
        />
      )}

      {/* Products loader - disabled for ps-section--visible */}
      {false && showProductsLoader && (
        <ProductsLoader 
          key={`loader-${loaderKeyRef.current}`}
          images={getAllImageUrls()} 
          onComplete={handleProductsLoaderComplete}
        />
      )}
      
      {/* Back button rendered outside section to ensure it's above everything */}
      {/* Show only on product gallery page when product is selected, hide when BagBuilder or LoadingScreen is shown */}
      {isDetail && selectedIdx !== null && !showBuilder && !showLoading && !loadingScreenVisible && (
        <button className="ps-back" type="button" onClick={handleBack} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          <img src={backArrowIcon} alt="" className="ps-back__icon" />
          <span className="ps-back__label">{t('BACK_STYLES_BTN')}</span>
        </button>
      )}
      {/* Render BagBuilder only after LoadingScreen is visible, but hidden behind LoadingScreen (z-index 1000 < 2000) */}
      {(loadingScreenVisible || showBuilder) && (
        <div style={{ 
          position: isMobile ? 'relative' : 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          zIndex: showLoading ? 1000 : 1 
        }}>
          <BagBuilder initialInitials={initialInitials} initialProductIdx={selectedProductIdx} />
        </div>
      )}
      {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      
      {/* Render ProductsShowcase - loader disabled for ps-section--visible */}
      {!showLoading && !showBuilder && !loadingScreenVisible && (
    <section 
      className={`ps-section${isVisible ? ' ps-section--visible' : ''}${isDetail ? ' ps-section--detail' : ''}`}
    >
      {!isDetail && (
        <div className="ps-inner">
          <h2 className="ps-title" dangerouslySetInnerHTML={{ __html: t('PRODUCTS_AGREE_STUNNING') }} />
          <div className="ps-grid">
            {products.map((item, idx) => (
              <button 
                className={`ps-card ${selectedProductIdx === idx ? 'ps-card--selected' : ''}`} 
                key={item.id} 
                onClick={() => handleSelect(idx)}
              >
                <div className="ps-thumb">
                  <img ref={el => (imgRefs.current[idx] = el)} src={item.image} alt={t(`PRODUCT_NAME_${item.id}`)} draggable={false} loading="lazy" decoding="async" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}


      {isDetail && selectedIdx !== null && (
        <>
          <div className="ps-detail">
            <div className={`ps-detail__left`}>
              <div className={`ps-detail__copy ${showCopy ? 'is-visible' : ''}`}>
                <h3 className="ps-detail__title">{t(`PRODUCT_NAME_${products[selectedIdx].id}`)}</h3>
              </div>
              <div className="ps-detail__left-img">
                <img ref={targetImgRef} src={products[selectedIdx].image} alt={t(`PRODUCT_NAME_${products[selectedIdx].id}`)} className="ps-detail__hero" draggable={false} style={isTablet ? {} : {opacity: showHero ? 1 : 0}} />
              </div>
              <button className={`ps-detail__confirm ${showCopy ? 'is-visible' : ''}`} type="button" onClick={handleConfirm}>{String(t('CONFIRM_SELECTION')).toUpperCase()}</button>
            </div>
            <div className={`ps-detail__right ${showRight ? 'is-visible' : ''}`} ref={detailRightRef}>
              <div className="ps-detail__column">
                {/* Main image first for tablet */}
                <div className="ps-detail__panel ps-detail__panel--main">
                  <img src={(products[selectedIdx].slides && products[selectedIdx].slides[0]) || products[selectedIdx].image} alt={t(`PRODUCT_NAME_${products[selectedIdx].id}`)} draggable={false} />
                </div>
                {/* Slides */}
                {(products[selectedIdx].slides || []).slice(1).map((img, i) => (
                  <div key={i} className="ps-detail__panel">
                    <img src={img} alt={`Slide ${i+1} of ${t(`PRODUCT_NAME_${products[selectedIdx].id}`)}`} draggable={false} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Fixed panel for tablet */}
          <div className={`ps-detail__fixed-panel ${showRight && !isTablet && !isMobile ? 'is-visible' : ''}`} ref={detailFixedPanelRef}>
            <h3 className="ps-detail__fixed-title">{t(`PRODUCT_NAME_${products[selectedIdx].id}`)}</h3>
            <button className="ps-detail__fixed-confirm" type="button" onClick={handleConfirm}>
              <span className="ps-detail__fixed-confirm-label">{String(t('CONFIRM_SELECTION')).toUpperCase()}</span>
              <div className="ps-detail__fixed-confirm-underline">
                <img src={underlineIcon} alt="" />
              </div>
            </button>
          </div>
        </>
      )}
    </section>
      )}
    </>
  );
};

export default ProductsShowcase;

